import { useCallback } from 'react'
import type { Puzzle } from '../types/puzzle'
import { isBlockedCell } from '../utils/puzzleUtils'

interface GridCellProps {
  /** 单元格内的字母 */
  value: string
  /** 是否为黑格 */
  isBlocked: boolean
  /** 是否被选中 */
  isSelected: boolean
  /** 是否正确 */
  isCorrect: boolean
  /** 是否在当前线索范围内 */
  isHighlighted: boolean
  /** 单元格编号 */
  number?: number
  /** 点击回调 */
  onClick: () => void
}

/**
 * 单个网格单元格组件
 */
function GridCell({
  value,
  isBlocked,
  isSelected,
  isCorrect,
  isHighlighted,
  number,
  onClick
}: GridCellProps) {
  // 确定单元格样式类名
  const cellClass = [
    'grid-cell relative',
    isBlocked && 'blocked',
    isSelected && 'selected',
    isCorrect && 'correct',
    isHighlighted && !isSelected && 'bg-brand-orange/20',
    !isBlocked && !value && 'empty',
    !isBlocked && value && !isCorrect && 'bg-zinc-800 text-white'
  ].filter(Boolean).join(' ')

  return (
    <div
      className={cellClass}
      onClick={onClick}
      role="button"
      tabIndex={isBlocked ? -1 : 0}
      aria-label={isBlocked ? 'blocked cell' : `cell ${number || ''}`}
    >
      {/* 单元格编号 */}
      {number && !isBlocked && (
        <span className="absolute top-0.5 left-1 text-[10px] leading-none text-zinc-500 font-sans font-bold">
          {number}
        </span>
      )}
      {/* 字母 */}
      {!isBlocked && (
        <span className={value ? 'animate-pop' : ''}>
          {value.toUpperCase()}
        </span>
      )}
    </div>
  )
}

interface PuzzleGridProps {
  /** 谜题数据 */
  puzzle: Puzzle
  /** 用户输入 */
  userInput: string[][]
  /** 当前选中单元格 */
  currentCell: [number, number]
  /** 输入方向 */
  direction: 'across' | 'down'
  /** 是否已完成 */
  isComplete: boolean
  /** 单元格点击回调 */
  onCellClick: (row: number, col: number) => void
}

/**
 * 5x5 填字网格组件
 */
export function PuzzleGrid({
  puzzle,
  userInput,
  currentCell,
  direction,
  isComplete,
  onCellClick
}: PuzzleGridProps) {
  const { grid, clues } = puzzle
  const { size, cells, blocked } = grid

  /**
   * 计算单元格编号
   */
  const getCellNumber = useCallback((row: number, col: number): number | undefined => {
    // 横向线索起点
    const acrossClue = clues.across.find(c => c.row === row && c.startCell?.[1] === col)
    if (acrossClue) return acrossClue.number
    
    // 纵向线索起点
    const downClue = clues.down.find(c => c.col === col && c.startCell?.[0] === row)
    if (downClue) return downClue.number
    
    return undefined
  }, [clues])

  /**
   * 检查单元格是否在当前高亮范围内
   */
  const isCellHighlighted = useCallback((row: number, col: number): boolean => {
    const [currentRow, currentCol] = currentCell
    
    if (direction === 'across') {
      return row === currentRow
    } else {
      return col === currentCol
    }
  }, [currentCell, direction])

  return (
    <div
      className="grid gap-1 mx-auto"
      style={{
        gridTemplateColumns: `repeat(${size}, minmax(0, 1fr))`,
        maxWidth: `${size * 80 + (size - 1) * 4}px`
      }}
      role="grid"
      aria-label="Crossword puzzle grid"
    >
      {Array.from({ length: size }).map((_, rowIdx) =>
        Array.from({ length: size }).map((_, colIdx) => {
          const isBlocked = isBlockedCell(blocked, rowIdx, colIdx)
          const value = userInput[rowIdx]?.[colIdx] || ''
          const isSelected = currentCell[0] === rowIdx && currentCell[1] === colIdx
          const isCorrect = isComplete && value.toUpperCase() === cells[rowIdx][colIdx].toUpperCase()
          const isHighlighted = isCellHighlighted(rowIdx, colIdx)
          const number = getCellNumber(rowIdx, colIdx)

          return (
            <GridCell
              key={`${rowIdx}-${colIdx}`}
              value={value}
              isBlocked={isBlocked}
              isSelected={isSelected}
              isCorrect={isCorrect}
              isHighlighted={isHighlighted}
              number={number}
              onClick={() => !isBlocked && onCellClick(rowIdx, colIdx)}
            />
          )
        })
      )}
    </div>
  )
}

